<template>
    <portal to="modals">
        <el-dialog :id="`${name}-barcode-scanner`" title="Scan Barcode" :visible="scanning" :append-to-body="appendToBody" @update:visible="close" @opened="scan" @closed="close">
            <div ref="viewport" class="viewport">
                <canvas class="drawing"></canvas>
            </div>
        </el-dialog>
    </portal>
</template>

<script>
    import Bugsnag from '@bugsnag/js';
    import Quagga from '@ericblade/quagga2';
    import randomString from '@/randomString';

    export default {
        name: 'Scanner',
        components: {},
        mixins: [],
        props: {
            name: {
                type: String,
                default: randomString(8),
            },
            scanning: {
                required: true,
                type: Boolean,
            },
            format: {
                required: true,
                type: String,
            },
            appendToBody: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                search: '',
            };
        },
        created() {
            const script = document.createElement('script');
            script.src = 'https://webrtc.github.io/adapter/adapter-6.0.4.js';
            script.type = 'text/javascript';
            document.head.append(script);
        },
        methods: {
            onProcessed(result) {
                const ctx = Quagga.canvas.ctx.overlay,
                    canvas = Quagga.canvas.dom.overlay;

                if (result) {
                    if (result.boxes) {
                        ctx.clearRect(0, 0, Number.parseInt(canvas.getAttribute('width'), 10), Number.parseInt(canvas.getAttribute('height'), 10));
                        for (const box of result.boxes.filter((box) => box !== result.box)) {
                            Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, ctx, { color: '#00ff00', lineWidth: 2 });
                        }
                    }

                    if (result.box) {
                        Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, ctx, { color: '#0000ff', lineWidth: 2 });
                    }

                    if (result.codeResult && result.codeResult.code) {
                        Quagga.ImageDebug.drawPath(result.line, { x: 'x', y: 'y' }, ctx, { color: '#ff0000', lineWidth: 3 });
                    }
                }
            },
            logCode(result) {
                if (result.codeResult) {
                    const getMedian = (arr) => {
                            arr.sort((a, b) => a - b);

                            const half = Math.floor(arr.length / 2);

                            // Odd length
                            if (arr.length % 2 === 1) {
                                return arr[half];
                            }

                            return (arr[half - 1] + arr[half]) / 2;
                        },
                        errors = result.codeResult.decodedCodes.filter((_) => _.error !== undefined).map((_) => _.error),
                        median = getMedian(errors);

                    if (median < 0.1) {
                        this.search = result.codeResult.code;
                        this.$nextTick(() => {
                            this.$emit('scanned', this.search);
                            this.$emit('update:scanning', false);
                            Quagga.stop();
                        });
                    }
                }
            },
            scan() {
                this.$nextTick(() => {
                    Quagga.init(
                        {
                            inputStream: {
                                type: 'LiveStream',
                                target: this.$refs.viewport,
                                constraints: {
                                    width: { min: 640 },
                                    height: { min: 480 },
                                    aspectRatio: { min: 1, max: 2 },
                                    facingMode: 'environment',
                                },
                            },
                            locator: {
                                patchSize: 'medium',
                                halfSample: true,
                            },
                            numOfWorkers: 2,
                            frequency: 10,
                            decoder: {
                                readers: [
                                    {
                                        format: `${this.format}_reader`,
                                        config: {},
                                    },
                                ],
                            },
                            locate: true,
                        },
                        (err, ...args) => {
                            if (err) {
                                this.close();

                                this.$notify.error({
                                    title: this.$t('Barcode Scanner Error'),
                                    message: this.$t("Unable to access device's camera."),
                                    duration: 0,
                                });
                                Bugsnag.notify(new Error('Barcode Scanner Error'), (event) => {
                                    event.severity = 'error';
                                    event.addMetadata('contextData', {
                                        err,
                                        args,
                                    });
                                });
                                return;
                            }
                            Quagga.start();
                        },
                    );
                    Quagga.onProcessed(this.onProcessed);
                    Quagga.onDetected(this.logCode);
                });
            },
            close() {
                Quagga.stop();
                this.$emit('update:scanning', false);
            },
        },
    };
</script>

<style>
    .overlay {
        overflow: hidden;
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.3);
    }

    .overlay__content {
        top: 50%;
        position: absolute;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 90%;
        max-height: 90%;
        max-width: 800px;
    }

    .overlay__close {
        position: absolute;
        right: 0;
        padding: 0.5rem;
        width: 2rem;
        height: 2rem;
        line-height: 2rem;
        text-align: center;
        background-color: white;
        cursor: pointer;
        border: 3px solid black;
        font-size: 1.5rem;
        margin: -1rem;
        border-radius: 2rem;
        z-index: 100;
        box-sizing: content-box;
    }

    .overlay__content video {
        width: 100%;
        height: 100%;
    }

    .overlay__content canvas {
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
    }

    .viewport {
        position: relative;
    }

    .viewport > canvas,
    .viewport > video {
        max-width: 100%;
        width: 100%;
    }

    canvas.drawing,
    canvas.drawingBuffer {
        position: absolute;
        left: 0;
        top: 0;
    }
</style>
